import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AuthService } from '../auth/auth.service';
import { LoginResponse } from '../auth/models/login-response';
import { ErrorService } from '@services/error.service';
import { MessageService } from '@services/message.service';
import { TypeToast } from '@models/messagedata';
import { ValidationResult } from '@models/validation-result';
import { DialogErrorService } from '@services/dialog-error.service';
import { LoadingService } from '@services/loading.service';

@Injectable()
export class HttpAuthErrorInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private errorService: ErrorService,
    private messageService: MessageService,
    private dialogErrorService: DialogErrorService,
    private loadingService: LoadingService,
    private router: Router,
  ) { }

  // no-explicit-any será desativado aqui porque realmente pode ser qualquer coisa
  /* eslint-disable @typescript-eslint/no-explicit-any */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    request = request.clone({
      setHeaders: {
        Authorization: `bearer ${this.authService.token}`
      }
    });

    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            const url = error.url ?? '';
            if (url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout')) {
              return throwError(() => error);
            }
            return this.handle401(request, next);
          }

          if (error.status === 413) {
            this.messageService.showToastError('Arquivo muito grande. O tamanho máximo permitido é 1MB.');
            this.loadingService.setIsLoading(false);
            return throwError(() => error);
          }

          if (error.status >= 400 && error.status < 500) {
            const validationResult = this.errorService.isValidationResultError(error.error) ?
              error.error as ValidationResult : null;

            // Se não for um validationResult, deve ser um erro HttpErrorResponse de permissão
            if (validationResult === null) {
              if (error.status === 403) {
                this.messageService.showToast({
                  message: 'Acesso negado',
                  typeToast: TypeToast.ERROR,
                  description: 'Você não tem permissão para realizar esta ação.'
                });

                this.loadingService.setIsLoading(false);
                return throwError(() => error);
              }

              this.errorService.goToError(
                'Endereço não encontrado',
                'O endereço buscado não foi encontrado no servidor'
              );

              this.loadingService.setIsLoading(false);
              return throwError(() => error);
            }

            if (error.status === 400) {
              this.messageService.showToast({
                message: "Erro na validação",
                typeToast: TypeToast.WARNING,
                description: validationResult.errors[0].errorMessage
              });
            }
            else
              this.messageService.showToast({
                message: "Erro na operação",
                typeToast: TypeToast.ERROR,
                description: validationResult.errors[0].errorMessage
              });

            this.loadingService.setIsLoading(false);
            return throwError(() => error);
          }

          if (Math.floor(error.status / 500) === 1) { // se for um erro na faixa de 500
            this.errorService.goToError(
              'Ocorreu um Erro',
              error.message
            );
          }

          this.loadingService.setIsLoading(false);
          return throwError(() => error);
        })
      );
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshAccessToken().pipe(
        switchMap((response: LoginResponse) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(response.accessToken);
          return next.handle(this.addToken(request, response.accessToken));
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.authService.logout();
          this.router.navigate(['/login'], { state: { mensagem: 'Sua sessão expirou. Faça login novamente.' } });
          return throwError(() => err);
        })
      );
    }

    // Outro request chegou enquanto o refresh estava em progresso: espera o token
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addToken(request, token!)))
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `bearer ${token}` }
    });
  }

}
